import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { propertiesService, Property } from '../services/properties.service';
import { expensesService, Expense } from '../services/expenses.service';
import toast from 'react-hot-toast';
import { FiDollarSign, FiCalendar, FiTrendingUp, FiPlus, FiMapPin, FiFileText, FiClock, FiActivity, FiShield, FiSearch, FiGrid, FiAlertCircle, FiLink } from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../config/api';
import * as ps from '../services/pocketsmith.service';
import { tw, monoFont } from '../utils/terminalTheme';

export const Dashboard: React.FC = () => {
  const [properties, setProperties] = useState<Property[]>([]);
  const [recentExpenses, setRecentExpenses] = useState<Expense[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [stats, setStats] = useState({
    totalProperties: 0,
    totalYTD: 0,
    totalAllTime: 0,
    recentTotal: 0,
    thisMonthTotal: 0,
    avgExpenseAmount: 0,
  });
  const { user } = useAuth();
  const [netWorth, setNetWorth] = useState<ps.GroupedAccountsResponse | null>(null);
  const [unmatchedExpenses, setUnmatchedExpenses] = useState<any[]>([]);
  const [pendingInvoices, setPendingInvoices] = useState<any[]>([]);

  useEffect(() => {
    loadProperties();
    ps.getGroupedAccounts().then(data => setNetWorth(data)).catch(() => {});
    api.get('/expenses/unmatched-discord').then(r => setUnmatchedExpenses(r.data)).catch(() => {});
    api.get('/expenses/pending-invoices').then(r => setPendingInvoices(r.data)).catch(() => {});
  }, []);

  const makeAdmin = async () => {
    try {
      await api.post('/setup/make-me-admin');
      toast.success('You are now an admin! Refreshing page...');
      setTimeout(() => window.location.reload(), 2000);
    } catch (error) {
      toast.error('Failed to make you an admin');
    }
  };

  const loadProperties = async () => {
    try {
      const [propertiesData, expensesData] = await Promise.all([
        propertiesService.getProperties(),
        expensesService.getExpenses()
      ]);

      setProperties(propertiesData);

      const sortedExpenses = expensesData.sort((a, b) =>
        new Date(b.expense_date).getTime() - new Date(a.expense_date).getTime()
      );

      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

      const recentExpensesList = sortedExpenses.filter(expense =>
        new Date(expense.expense_date) >= thirtyDaysAgo
      );

      setRecentExpenses(recentExpensesList.slice(0, 10));

      const currentYear = new Date().getFullYear();
      const currentMonth = new Date().getMonth();

      const ytdExpenses = expensesData.filter(expense => {
        return new Date(expense.expense_date).getFullYear() === currentYear;
      });

      const thisMonthExpenses = expensesData.filter(expense => {
        const d = new Date(expense.expense_date);
        return d.getFullYear() === currentYear && d.getMonth() === currentMonth;
      });

      const totalYTD = ytdExpenses.reduce((sum, e) => sum + e.amount, 0);
      const totalAllTime = expensesData.reduce((sum, e) => sum + e.amount, 0);
      const recentTotal = recentExpensesList.reduce((sum, e) => sum + e.amount, 0);
      const thisMonthTotal = thisMonthExpenses.reduce((sum, e) => sum + e.amount, 0);
      const avgExpenseAmount = expensesData.length > 0 ? totalAllTime / expensesData.length : 0;

      setStats({ totalProperties: propertiesData.length, totalYTD, totalAllTime, recentTotal, thisMonthTotal, avgExpenseAmount });
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setIsLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
  };

  if (isLoading) {
    return (
      <div className={tw.loadingContainer}>
        <div className={tw.loading}></div>
      </div>
    );
  }

  return (
    <div className={tw.page}>
      {/* Terminal Header */}
      <div className="mb-8 flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold font-mono text-white" style={monoFont}>
            <span className="text-cyan-400">~/</span>dashboard
            <span className="text-gray-600 text-sm font-normal ml-3">
              <span className="text-green-500">$</span> status --overview
            </span>
          </h1>
          <p className="text-gray-500 text-sm font-mono mt-1">Recent activity and expense overview</p>
        </div>
        {user && user.role !== 'ADMIN' && user.role !== 'admin' && (
          <button onClick={makeAdmin} className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-md font-mono text-sm transition-colors">
            <FiShield className="w-4 h-4" />
            Make Me Admin
          </button>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-6">
        <div className={tw.statCard}>
          <p className={tw.statLabel}>This Month</p>
          <p className={tw.statValueCyan}>{formatCurrency(stats.thisMonthTotal)}</p>
        </div>
        <div className={tw.statCard}>
          <p className={tw.statLabel}>Last 30 Days</p>
          <p className="text-2xl font-bold font-mono text-green-500">{formatCurrency(stats.recentTotal)}</p>
        </div>
        <div className={tw.statCard}>
          <p className={tw.statLabel}>YTD Total</p>
          <p className="text-2xl font-bold font-mono text-amber-400">{formatCurrency(stats.totalYTD)}</p>
        </div>
        <div className={tw.statCard}>
          <p className={tw.statLabel}>Avg Expense</p>
          <p className="text-2xl font-bold font-mono text-purple-400">{formatCurrency(stats.avgExpenseAmount)}</p>
        </div>
      </div>

      {/* Net Worth Card */}
      {netWorth && (
        <div className="bg-gray-800/80 border border-gray-700/50 rounded-lg p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-mono text-gray-400 uppercase tracking-wider">Net Worth</h2>
            <Link to="/pocketsmith/accounts" className="text-xs text-cyan-400 hover:text-cyan-300 font-mono transition-colors">
              View Accounts &rarr;
            </Link>
          </div>
          <div className="text-3xl font-bold font-mono mb-4" style={{ color: netWorth.grand_total >= 0 ? '#34d399' : '#ef4444' }}>
            {formatCurrency(netWorth.grand_total)}
          </div>
          <div className="space-y-3">
            {netWorth.groups.map(group => {
              const pct = netWorth.grand_total !== 0 ? Math.abs(group.subtotal / netWorth.grand_total * 100) : 0;
              return (
                <div key={group.key}>
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-xs text-gray-500 font-mono">{group.label} ({group.account_count})</span>
                    <span className="text-xs font-mono font-medium" style={{ color: group.color }}>
                      {formatCurrency(group.subtotal)}
                    </span>
                  </div>
                  <div className="w-full bg-gray-700/50 rounded-full h-1.5">
                    <div
                      className="h-1.5 rounded-full transition-all"
                      style={{ width: Math.min(pct, 100) + '%', backgroundColor: group.color }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}



      {/* Pending Invoices - awaiting payment */}
      {pendingInvoices.length > 0 && (
        <div className="bg-gray-800/80 border border-fuchsia-500/40 rounded-lg p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-mono text-fuchsia-300 uppercase tracking-wider flex items-center gap-2">
              <FiAlertCircle className="w-4 h-4" />
              Pending Invoices - Awaiting Your Payment
              <span className="px-2 py-0.5 bg-fuchsia-500/20 text-fuchsia-200 rounded-full text-[10px] font-bold">{pendingInvoices.length}</span>
            </h2>
          </div>
          <div className="space-y-2">
            {pendingInvoices.map((inv: any) => (
              <div key={inv.invoice_id} className="flex items-center justify-between bg-gray-900/50 rounded-lg px-4 py-3 border border-fuchsia-900/30">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-fuchsia-500/15 border border-fuchsia-500/40 flex items-center justify-center flex-shrink-0">
                    <FiAlertCircle className="w-4 h-4 text-fuchsia-400" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-mono text-white font-medium">{inv.vendor}</span>
                      <span className="text-[10px] font-mono px-1.5 py-0.5 bg-fuchsia-500/15 text-fuchsia-300 border border-fuchsia-500/30 rounded">Invoice #{inv.invoice_id}</span>
                    </div>
                    <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                      {inv.property_name && (
                        <Link to={`/properties/${inv.property_id}`} className="text-[11px] font-mono text-cyan-400 hover:text-cyan-300">
                          {inv.property_name}
                        </Link>
                      )}
                      {inv.payment_method && (
                        <span className="text-[10px] font-mono text-fuchsia-300">{inv.payment_method}{inv.payment_details ? ': ' + inv.payment_details : ''}</span>
                      )}
                      {inv.days_old !== null && inv.days_old > 0 && (
                        <span className="text-[10px] font-mono text-gray-600">{inv.days_old}d old</span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-base font-mono font-bold text-fuchsia-300">{new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(inv.amount)}</span>
                </div>
              </div>
            ))}
          </div>
          <p className="text-[10px] font-mono text-gray-600 mt-3">After paying, return to Discord with a screenshot and say "paid" to close the invoice and log the expense.</p>
        </div>
      )}

      {/* Discord Expenses — Unmatched Queue */}
      {unmatchedExpenses.length > 0 && (
        <div className="bg-gray-800/80 border border-amber-500/30 rounded-lg p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-mono text-amber-400 uppercase tracking-wider flex items-center gap-2">
              <FiAlertCircle className="w-4 h-4" />
              Discord Expenses — Awaiting Bank Match
              <span className="px-2 py-0.5 bg-amber-500/20 text-amber-300 rounded-full text-[10px] font-bold">{unmatchedExpenses.length}</span>
            </h2>
          </div>
          <div className="space-y-2">
            {unmatchedExpenses.map((exp: any) => (
              <div key={exp.expense_id} className="flex items-center justify-between bg-gray-900/50 rounded-lg px-4 py-3 border border-gray-700/30">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-amber-500/15 border border-amber-500/30 flex items-center justify-center flex-shrink-0">
                    <FiAlertCircle className="w-4 h-4 text-amber-400" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-mono text-white font-medium">{exp.vendor}</span>
                      <span className="text-[10px] font-mono px-1.5 py-0.5 bg-purple-500/15 text-purple-400 border border-purple-500/30 rounded">Discord</span>
                    </div>
                    <div className="flex items-center gap-2 mt-0.5">
                      {exp.property_name && (
                        <Link to={`/properties/${exp.property_id}`} className="text-[11px] font-mono text-cyan-400 hover:text-cyan-300">
                          {exp.property_name}
                        </Link>
                      )}
                      <span className="text-[10px] font-mono text-gray-600">{exp.expense_date}</span>
                      {exp.days_old > 3 && (
                        <span className="text-[10px] font-mono text-amber-500">{exp.days_old}d old</span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm font-mono font-bold text-white">{new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(exp.amount)}</span>
                  <span className="text-[10px] font-mono text-gray-600 flex items-center gap-1">
                    <FiLink className="w-3 h-3" /> no bank match
                  </span>
                </div>
              </div>
            ))}
          </div>
          <p className="text-[10px] font-mono text-gray-600 mt-3">Auto-retries every 6 hours when PocketSmith syncs. Expenses will clear once the bank transaction posts.</p>
        </div>
      )}

      {/* Recent Expenses */}
      <div className={tw.tableWrap}>
        <div className="px-5 py-3 border-b border-gray-700/50 flex items-center justify-between">
          <h2 className="text-sm font-mono text-gray-400 uppercase tracking-wider">Recent Expenses</h2>
          <Link to="/properties" className="text-xs text-cyan-400 hover:text-cyan-300 font-mono transition-colors">
            View All Properties &rarr;
          </Link>
        </div>

        {recentExpenses.length === 0 ? (
          <div className={tw.empty}>
            <FiDollarSign className="w-12 h-12 text-gray-700 mx-auto mb-4" />
            <p className="mb-4">No recent expenses</p>
            <Link to="/properties" className="text-cyan-400 hover:text-cyan-300 font-mono text-sm">
              Go to Properties to add expenses
            </Link>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="border-b border-gray-700/30">
                  <th className={tw.tableHeader}>Date</th>
                  <th className={tw.tableHeader}>Property</th>
                  <th className={tw.tableHeader}>Vendor</th>
                  <th className={tw.tableHeader}>Description</th>
                  <th className={tw.tableHeaderRight}>Amount</th>
                  <th className={tw.tableHeader}>Actions</th>
                </tr>
              </thead>
              <tbody className={tw.tableDivider}>
                {recentExpenses.map((expense) => {
                  const property = properties.find(p => p.id === expense.property_id);
                  return (
                    <tr key={expense.id} className={tw.tableRow}>
                      <td className={tw.tableCell}>
                        <span className="text-sm text-gray-400 font-mono">
                          {new Date(expense.expense_date).toLocaleDateString()}
                        </span>
                      </td>
                      <td className={tw.tableCell}>
                        {property ? (
                          <Link to={`/properties/${property.id}`} className="text-sm text-white hover:text-cyan-400 font-mono transition-colors">
                            {property.name}
                          </Link>
                        ) : (
                          <span className="text-sm text-gray-600 font-mono">General</span>
                        )}
                      </td>
                      <td className={tw.tableCell}>
                        <span className="text-sm text-gray-400 font-mono">{expense.vendor || 'N/A'}</span>
                      </td>
                      <td className={tw.tableCell}>
                        <div className="flex items-center">
                          <span className="text-sm text-gray-400 font-mono truncate max-w-xs">
                            {expense.description || 'N/A'}
                          </span>
                          {expense.document_id && (
                            <FiFileText className="w-3.5 h-3.5 text-cyan-400 ml-2 flex-shrink-0" title="Has document" />
                          )}
                        </div>
                      </td>
                      <td className={tw.tableCellRight}>
                        <span className="text-sm font-mono font-medium text-white">
                          {formatCurrency(expense.amount)}
                        </span>
                      </td>
                      <td className={tw.tableCell}>
                        {property && (
                          <Link to={`/properties/${property.id}/expenses`} className={tw.btnGhost}>
                            View All
                          </Link>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Quick Links */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-3">
        <Link to="/properties" className="bg-gray-800/80 border border-gray-700/50 p-5 rounded-lg hover:border-cyan-500/30 transition-all group">
          <div className="flex items-center">
            <FiMapPin className="w-6 h-6 text-cyan-400 group-hover:text-cyan-300" />
            <div className="ml-4">
              <h3 className="text-sm font-mono font-medium text-white">Properties</h3>
              <p className="text-xs text-gray-500 font-mono">{stats.totalProperties} total properties</p>
            </div>
          </div>
        </Link>

        <Link to="/documents" className="bg-gray-800/80 border border-gray-700/50 p-5 rounded-lg hover:border-cyan-500/30 transition-all group">
          <div className="flex items-center">
            <FiFileText className="w-6 h-6 text-blue-400 group-hover:text-blue-300" />
            <div className="ml-4">
              <h3 className="text-sm font-mono font-medium text-white">Documents</h3>
              <p className="text-xs text-gray-500 font-mono">Manage documents</p>
            </div>
          </div>
        </Link>

        <Link to="/upload" className="bg-gray-800/80 border border-gray-700/50 p-5 rounded-lg hover:border-cyan-500/30 transition-all group">
          <div className="flex items-center">
            <FiPlus className="w-6 h-6 text-green-500 group-hover:text-green-400" />
            <div className="ml-4">
              <h3 className="text-sm font-mono font-medium text-white">Upload</h3>
              <p className="text-xs text-gray-500 font-mono">Add new documents</p>
            </div>
          </div>
        </Link>
      </div>

      {/* Experimental Features */}
      <div className="mt-6">
        <h2 className="text-xs font-mono text-gray-600 uppercase tracking-wider mb-3 px-1">Experimental</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Link to="/rental-portfolio" className="bg-gray-800/80 border border-dashed border-gray-700/50 p-5 rounded-lg hover:border-purple-500/30 transition-all group">
            <div className="flex items-center">
              <FiGrid className="w-6 h-6 text-purple-400 group-hover:text-purple-300" />
              <div className="ml-4">
                <h3 className="text-sm font-mono font-medium text-white">Rental Portfolio Analysis</h3>
                <p className="text-xs text-gray-500 font-mono">Market data & tenant management</p>
                <span className="inline-block mt-1 px-2 py-0.5 text-[10px] font-mono bg-purple-500/15 text-purple-400 border border-purple-500/30 rounded">Beta</span>
              </div>
            </div>
          </Link>
        </div>
      </div>
    </div>
  );
};
